import React, { useState, useEffect, useRef } from 'react';
import { BookOpen, Clock } from 'lucide-react';

export default function Articles() {
  const sectionRef = useRef(null);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => entries.forEach((e) => e.isIntersecting && e.target.classList.add('visible')),
      { threshold: 0.1 }
    );
    const el = sectionRef.current;
    if (el) el.querySelectorAll('.reveal').forEach((node) => observer.observe(node));
    return () => observer.disconnect();
  }, []);

  return (
    <section id="articles" className="py-24 lg:py-32 bg-white" data-testid="articles-section" ref={sectionRef}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

        {/* Header */}
        <div className="mb-12 reveal">
          <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
            <h2 className="font-heading text-4xl md:text-5xl text-[#2C362A] leading-tight">
              Resources
            </h2>
            <span className="inline-flex items-center gap-2 bg-[#E6EBE4] text-[#708A72] font-body text-xs font-semibold px-4 py-2 rounded-full flex-shrink-0">
              <Clock size={13} strokeWidth={1.5} />
              More Coming Soon
            </span>
          </div>
        </div>

        {/* Featured article — clickable anywhere on the box */}
        <div className="mb-6 reveal">
          <div
            className="bg-[#FAF9F6] border border-[#D1D8D0] rounded-2xl overflow-hidden cursor-pointer hover:bg-[#F0F2EC] transition-colors duration-200"
            onClick={() => setExpanded(!expanded)}
            role="button"
            aria-expanded={expanded}
            data-testid="article-emotional-overwhelm"
          >
            <div className="p-6 sm:p-8">
              <div className="flex items-start justify-between gap-4">
                <div className="flex items-start gap-4">
                  <div className="w-9 h-9 rounded-lg bg-[#E6EBE4] flex items-center justify-center flex-shrink-0 mt-0.5">
                    <BookOpen size={15} strokeWidth={1.5} className="text-[#708A72]" />
                  </div>
                  <div>
                    <span className="font-body text-xs text-[#708A72] tracking-wide uppercase mb-1 block">Article</span>
                    <h3 className="font-heading text-2xl md:text-3xl text-[#2C362A] leading-snug">
                      Understanding Emotional Overwhelm
                    </h3>
                    <p className="font-body text-sm text-[#5B6E56] mt-1 italic">
                      How our emotions live in the body — and what therapy can offer
                    </p>
                  </div>
                </div>
                <span className="flex-shrink-0 font-body text-xs text-[#708A72] mt-1 select-none">
                  {expanded ? 'Close ↑' : 'Read ↓'}
                </span>
              </div>

              {expanded && (
                <div className="mt-6 pt-6 border-t border-[#D1D8D0] space-y-5">
                  <p className="font-body text-sm text-[#5B6E56] leading-relaxed">
                    Life can be incredibly challenging, and at times <strong className="font-semibold text-[#2C362A]">our emotional world can feel like more than we know how to manage</strong>. When we haven't learned how to work with our emotions, a major crisis can push us to our limits. Intense feelings can surface, feelings that are difficult to understand, accept or express.
                  </p>
                  <p className="font-body text-sm text-[#5B6E56] leading-relaxed">
                    In these moments, the body often steps in with its own protective strategies. It may shut down, numb out or push overwhelming emotions outside of conscious awareness. This is the body doing what it knows best: keeping us safe.
                  </p>

                  <h4 className="font-heading text-lg md:text-xl text-[#2C362A] pt-2">
                    How the Body Responds to Overwhelm
                  </h4>
                  <p className="font-body text-sm text-[#5B6E56] leading-relaxed">
                    Emotions don't simply disappear. They are energetic experiences that create physical sensations of tightness, heaviness, heat, pressure, restlessness. When these sensations are repeatedly pushed aside, <strong className="font-semibold text-[#2C362A]">they can become "stuck" in our bodies</strong>.
                  </p>
                  <p className="font-body text-sm text-[#5B6E56] leading-relaxed">
                    Over time, this can influence muscle tension, stress hormone levels and our nervous system. Unprocessed emotions can leave the body in a subtle but persistent state of activation, contributing to fatigue, tension and a general feeling of unease.
                  </p>

                  <h4 className="font-heading text-lg md:text-xl text-[#2C362A] pt-2">
                    What Your Body Is Trying to Tell You
                  </h4>
                  <p className="font-body text-sm text-[#5B6E56] leading-relaxed">
                    <strong className="font-semibold text-[#2C362A]">Stress symptoms are not failures, they are forms of communication.</strong> They reflect the body's wisdom, drawing attention to experiences that need care rather than avoidance. These signals can be invitations to slow down, to listen inwardly and to reconnect with what has been held beneath the surface.
                  </p>

                  <h4 className="font-heading text-lg md:text-xl text-[#2C362A] pt-2">
                    How Therapy Restores Balance
                  </h4>
                  <p className="font-body text-sm text-[#5B6E56] leading-relaxed">
                    Therapy offers a safe, gentle space to explore these emotional and physical experiences. Through awareness and guided support, it becomes possible to understand what you're feeling, notice where emotions are held in the body, reduce overwhelm, process what has been pushed down and restore a sense of balance and connection.
                  </p>
                  <p className="font-body text-sm text-[#5B6E56] leading-relaxed">
                    As emotional processing becomes more accessible, resilience grows. The body softens. The nervous system steadies. And the inner world becomes a place you can navigate with greater confidence and compassion.
                  </p>

                  <p className="font-body text-xs text-[#A8B5A3] pt-3 border-t border-[#D1D8D0]/50">
                    &copy; Donna Gordon, MA (Psychotherapist)
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Coming soon placeholder cards */}
        <div className="grid sm:grid-cols-3 gap-5">
          {[0, 1, 2].map((i) => (
            <div
              key={i}
              className="bg-[#FAF9F6] border border-[#D1D8D0] rounded-2xl p-6 flex flex-col items-center justify-center text-center min-h-[120px] reveal"
              style={{ transitionDelay: `${i * 0.1}s` }}
              data-testid={`article-placeholder-${i}`}
            >
              <div className="w-8 h-8 rounded-lg bg-[#E6EBE4] flex items-center justify-center mb-3">
                <BookOpen size={14} strokeWidth={1.5} className="text-[#A8B5A3]" />
              </div>
              <span className="font-body text-xs text-[#A8B5A3] tracking-wide">Coming Soon</span>
            </div>
          ))}
        </div>

        <div className="mt-10 text-center reveal">
          <p className="font-body text-sm text-[#5B6E56] italic">
            To be notified when new articles are published,{' '}
            <button
              onClick={(e) => { e.stopPropagation(); document.querySelector('#contact')?.scrollIntoView({ behavior: 'smooth' }); }}
              className="text-[#708A72] underline underline-offset-2 hover:no-underline transition-all"
              data-testid="articles-contact-link"
            >
              get in touch
            </button>.
          </p>
        </div>
      </div>
    </section>
  );
}
